import { useEffect, useState } from "react";
import { ServerWakingBanner } from "@/components/AppBanners";
import { warmUp } from "@/lib/api";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { LANGS, getLang } from "@/i18n";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Building2, CheckCircle2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { formatApiErrorDetail } from "@/lib/format";
import { EditionCards } from "@/components/EditionCards";

const Field = ({ id, label, ...props }) => (
  <div className="space-y-1.5">
    <Label htmlFor={id}>{label}</Label>
    <Input id={id} required className="h-11" data-testid={`register-${id}-input`} {...props} />
  </div>
);

export default function Register() {
  const { registerCompany } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ ragione_sociale: "", email: "", password: "" });
  const [edizione, setEdizione] = useState("completa");
  const [accept, setAccept] = useState(false);
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  useEffect(() => { warmUp(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (form.password.length < 8) return toast.error("La password deve avere almeno 8 caratteri.");
    if (!accept) return toast.error("Devi accettare Termini e Informativa Privacy.");
    setLoading(true);
    try {
      await registerCompany({ ...form, ragione_sociale: form.ragione_sociale.trim(), email: form.email.trim(), accept_terms: true, edizione });
      toast.success(edizione === "semplice"
        ? "Benvenuto! Il tuo Gestionale semplice è pronto: agenda, assistente IA e rubrica clienti."
        : "Benvenuto! Prova pure il gestionale: i dati dell'azienda li chiediamo solo quando servono.");
      navigate("/");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Registrazione non riuscita");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-[5fr_7fr] bg-background relative">
      <div className="absolute top-4 right-4 z-10"><LanguageSwitcher /></div>
      <div className="hidden lg:flex flex-col justify-between p-12 bg-slate-900 text-white relative overflow-hidden">
        <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-primary/30 blur-3xl" />
        <div className="relative flex items-center gap-3">
          <img src="/k-gestionale.png" alt="K Gestionale" className="h-12 w-12 rounded-xl" />
          <div className="font-heading font-extrabold text-lg tracking-tight">K Gestionale</div>
        </div>
        <div className="relative space-y-6">
          <h2 className="font-heading text-4xl font-extrabold leading-tight tracking-tight">La tua impresa,<br />il tuo spazio<br />riservato.</h2>
          <ul className="space-y-3 text-slate-200">
            {["Scegli tra App completa e Gestionale semplice", "Dati separati e visibili solo alla tua azienda", "Prova gratuita di 14 giorni, accesso immediato"].map((f) => (
              <li key={f} className="flex items-center gap-3 text-sm"><CheckCircle2 className="w-5 h-5 text-emerald-400 shrink-0" />{f}</li>
            ))}
          </ul>
        </div>
        <div className="relative text-xs text-slate-400" translate="no">{LANGS.find((l) => l.code === getLang())?.label} · € EUR</div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12">
        <form onSubmit={submit} className="w-full max-w-sm space-y-6 animate-in-up" data-testid="register-form">
          <div>
            <div className="w-11 h-11 rounded-xl bg-primary/10 text-primary flex items-center justify-center mb-4"><Building2 className="w-6 h-6" /></div>
            <h1 className="font-heading text-3xl font-extrabold tracking-tight">Prova K Gestionale</h1>
            <div className="inline-flex items-center gap-1.5 mt-3 rounded-full bg-primary/10 text-primary text-xs font-semibold px-3 py-1" data-testid="register-trial-badge">Gratis per 14 giorni · nessuna carta richiesta</div>
            <p className="text-muted-foreground text-sm mt-2">Bastano 3 dati. P.IVA e indirizzo te li chiediamo solo quando crei il primo preventivo, fattura o DDT.</p>
          </div>
          <div className="space-y-2">
            <div className="text-sm font-medium">Scegli la versione</div>
            <EditionCards value={edizione} onChange={setEdizione} variant="select" />
            <p className="text-xs text-muted-foreground">Dal Gestionale semplice puoi passare all'app completa quando vuoi, da Impostazioni.</p>
          </div>
          <div className="space-y-4">
            <ServerWakingBanner inline />
            <Field id="ragione_sociale" label="Nome della ditta" value={form.ragione_sociale} onChange={set("ragione_sociale")} placeholder="Rossi Costruzioni" autoComplete="organization" />
            <Field id="email" label="Email" type="email" value={form.email} onChange={set("email")} placeholder="nome@azienda.it" autoComplete="email" />
            <Field id="password" label="Password" type="password" value={form.password} onChange={set("password")} placeholder="Minimo 8 caratteri" autoComplete="new-password" />
          </div>
          <label className="flex items-start gap-2.5 text-sm cursor-pointer">
            <Checkbox required checked={accept} onCheckedChange={(v) => setAccept(!!v)} data-testid="register-accept-terms" className="mt-0.5 h-5 w-5" />
            <span>Accetto i <Link to="/termini" className="text-primary underline inline-block py-2">Termini</Link> e l'<Link to="/privacy" className="text-primary underline inline-block py-2">Informativa Privacy</Link>.</span>
          </label>
          <Button type="submit" className="w-full h-11" disabled={loading} data-testid="register-submit-button">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Prova subito il gestionale"}
          </Button>
          <p className="text-sm text-center text-muted-foreground">
            Hai già un account? <Link to="/login" className="text-primary font-medium underline" data-testid="register-login-link">Accedi</Link>
          </p>
        </form>
      </div>
    </div>
  );
}
